import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, Info, Loader2, Lock, AlertCircle } from 'lucide-react';
import { companies } from '../api/companies';
import { carriers as carriersApi } from '../api/carriers';
import { orderIntegrations } from '../api/order-integrations';

/**
 * First-run setup, shown once to a company admin whose company has no ship-from
 * address yet. The server decides that (`showSetupWizard` on the login
 * response); this component only renders it.
 *
 * Every step writes through an endpoint that already exists — company profile,
 * carrier credentials, order integrations. Nothing here is a new way to save the
 * same thing, so a change to any of those screens carries over.
 *
 * Steps 2 and 3 are skippable and say what stays unavailable if you skip; step 1
 * is not, because a rate cannot be quoted without an origin.
 */

/**
 * Does this company have a usable ship-from address?
 *
 * Mirrors the server's own check (auth.service's companyHasOrigin): the JSON
 * blob can exist while being empty, and an empty one quotes nothing. Exported so
 * the banner and the wizard cannot drift on what "set up" means.
 */
export function hasShippingOrigin(origin: any): boolean {
  return Boolean(origin && origin.street1 && origin.city && origin.zip);
}

interface Props {
  open: boolean;
  /** Called when the wizard is dismissed or finished — clears the one-shot flag. */
  onClose: () => void;
  /**
   * Called once the origin address has been saved. The banner listens for this
   * rather than re-fetching the profile on a timer.
   */
  onAddressSaved?: () => void;
}

type Step = 1 | 2 | 3 | 4;

interface CarrierOption {
  id: string;
  code: string;
  name: string;
}

const PLATFORMS = ['Magento', 'Shopify', 'WooCommerce', 'OpenCart'];

const labelCls =
  'text-[10px] font-semibold text-slate-400 uppercase tracking-widest';
const inputCls =
  'w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-200 transition-all';
const primaryCls =
  'flex items-center gap-2 bg-slate-900 text-white rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-slate-800 transition-colors disabled:bg-slate-100 disabled:text-slate-400';
const secondaryCls =
  'bg-slate-100 text-slate-600 rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-slate-200 transition-colors';

export default function SetupWizardModal({ open, onClose, onAddressSaved }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // step 1
  const [address, setAddress] = useState({
    street1: '', street2: '', city: '', state: '', zip: '', country: 'US',
  });
  // step 2
  const [carrierList, setCarrierList] = useState<CarrierOption[]>([]);
  const [carrierId, setCarrierId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  // step 3
  const [platform, setPlatform] = useState('Magento');
  const [storeUrl, setStoreUrl] = useState('');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);

  const [done, setDone] = useState({ carrier: false, store: false });

  /**
   * Only carriers enabled on the platform, and never Free Shipping — it is a
   * shipping method, not an account anyone holds credentials for. Mirrors the
   * exclusion in the API's getAvailableCarriers.
   */
  useEffect(() => {
    if (!open) return;
    carriersApi
      .getSettings()
      .then((rows: any[]) => {
        const usable = (rows || [])
          .filter((c) => {
            const code = String(c.code || '').toLowerCase();
            const name = String(c.name || '').toLowerCase();
            return code !== 'freeshipping' && code !== 'free_shipping' && !name.includes('free shipping');
          })
          .map((c) => ({ id: c.id || c._id, code: c.code, name: c.name }));
        setCarrierList(usable);
      })
      .catch(() => setCarrierList([]));
  }, [open]);

  if (!open) return null;

  const fail = (err: any, fallback: string) =>
    setError(err?.response?.data?.message || err?.message || fallback);

  const saveAddress = async () => {
    setSaving(true);
    setError(null);
    try {
      await companies.updateProfile({
        defaultShippingOrigin: {
          street1: address.street1.trim(),
          street2: address.street2.trim() || undefined,
          city: address.city.trim(),
          state: address.state.trim(),
          zip: address.zip.trim(),
          country: address.country.trim() || 'US',
        },
      });
      onAddressSaved?.();
      setStep(2);
    } catch (err: any) {
      // The address is what unlocks quoting, so a failure here has to stop the
      // wizard rather than carry on to a step that depends on it.
      fail(err, "Couldn't save the address. Nothing was changed — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const saveCarrier = async () => {
    setSaving(true);
    setError(null);
    try {
      await carriersApi.saveCredentials({
        carrierId,
        credentials: { apiKey: apiKey.trim(), apiSecret: secretKey.trim() },
      });
      setDone((d) => ({ ...d, carrier: true }));
      setStep(3);
    } catch (err: any) {
      fail(err, 'The carrier did not accept those keys. Nothing was saved.');
    } finally {
      setSaving(false);
    }
  };

  const saveStore = async () => {
    setSaving(true);
    setError(null);
    try {
      await orderIntegrations.create({
        name: platform,
        platform,
        storeUrl: storeUrl.trim(),
        credentials: { token: token.trim() },
      });
      setDone((d) => ({ ...d, store: true }));
      setStep(4);
    } catch (err: any) {
      fail(err, "Couldn't reach that store. Nothing was saved.");
    } finally {
      setSaving(false);
    }
  };

  const addressReady =
    address.street1.trim() && address.city.trim() && address.state.trim() && address.zip.trim();
  const carrierReady = carrierId && apiKey.trim() && secretKey.trim();
  const storeReady = platform && storeUrl.trim() && token.trim();

  const stepper = [
    { n: 1, label: 'Address' },
    { n: 2, label: 'Carrier' },
    { n: 3, label: 'Store' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-full max-w-lg p-6 space-y-5 max-h-[85vh] overflow-y-auto">

        {step < 4 && (
          <>
            <div className="flex items-center">
              {stepper.map((s, i) => {
                const complete = step > s.n;
                const current = step === s.n;
                return (
                  <div key={s.n} className={`flex items-center ${i < stepper.length - 1 ? 'flex-grow' : ''}`}>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className={`w-6 h-6 rounded-full border-[1.5px] flex items-center justify-center ${
                        complete || current ? 'bg-slate-900 border-slate-900' : 'bg-white border-slate-200'
                      }`}>
                        {complete
                          ? <Check size={12} className="text-white" strokeWidth={3} />
                          : <span className={`text-[11px] font-semibold ${current ? 'text-white' : 'text-slate-400'}`}>{s.n}</span>}
                      </div>
                      <span className={`text-xs whitespace-nowrap ${current ? 'font-semibold text-slate-900' : 'text-slate-400'}`}>{s.label}</span>
                    </div>
                    {i < stepper.length - 1 && (
                      <div className={`flex-grow h-[1.5px] mx-2.5 ${complete ? 'bg-slate-900' : 'bg-slate-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="h-px bg-slate-100" />
          </>
        )}

        {error && (
          <div className="flex items-start gap-2.5 bg-red-50 rounded-xl px-3.5 py-3">
            <AlertCircle size={15} className="text-red-600 shrink-0 mt-0.5" />
            <span className="text-xs text-slate-700 leading-relaxed">{error}</span>
          </div>
        )}

        {step === 1 && (
          <>
            <div className="space-y-1.5">
              <h2 className="text-lg font-semibold text-slate-900">Where do you ship from?</h2>
              <p className="text-[13px] text-slate-500 leading-relaxed">
                Every rate is quoted from this address, so it has to be the warehouse or dock the parcel actually leaves.
              </p>
            </div>

            <div className="space-y-3.5">
              <div className="space-y-1.5">
                <label className={labelCls}>Street address</label>
                <input className={inputCls} value={address.street1} autoFocus
                  onChange={(e) => setAddress({ ...address, street1: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Suite, unit, dock <span className="normal-case tracking-normal font-normal text-slate-300">— optional</span></label>
                <input className={inputCls} value={address.street2}
                  onChange={(e) => setAddress({ ...address, street2: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <label className={labelCls}>City</label>
                  <input className={inputCls} value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>State</label>
                  <input className={inputCls} value={address.state} maxLength={2}
                    onChange={(e) => setAddress({ ...address, state: e.target.value.toUpperCase() })} />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>ZIP</label>
                  <input className={inputCls} value={address.zip}
                    onChange={(e) => setAddress({ ...address, zip: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2.5 bg-slate-50 rounded-xl px-3.5 py-3">
              <Lock size={15} className="text-slate-400 shrink-0 mt-0.5" />
              <span className="text-xs text-slate-500 leading-relaxed">
                <span className="font-medium text-slate-700">This step can't be skipped.</span>{' '}
                Get Quotation, Orders History and tracking stay locked until an origin address is saved.
              </span>
            </div>

            <div className="h-px bg-slate-100" />
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Step 1 of 3</span>
              <button className={primaryCls} disabled={!addressReady || saving} onClick={saveAddress}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                Save and continue
                {!saving && <ArrowRight size={14} />}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="space-y-1.5">
              <h2 className="text-lg font-semibold text-slate-900">Connect a carrier</h2>
              <p className="text-[13px] text-slate-500 leading-relaxed">
                Your own account with the carrier. We use it to quote live rates and buy labels — the negotiated prices stay yours.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className={labelCls}>Carrier</label>
              <div className="grid grid-cols-2 gap-2">
                {carrierList.map((c) => (
                  <button key={c.id} type="button" onClick={() => setCarrierId(c.id)}
                    className={`rounded-xl border-[1.5px] px-2.5 py-3 text-xs text-center leading-tight transition-colors ${
                      carrierId === c.id
                        ? 'border-slate-900 bg-slate-50 text-slate-900 font-semibold'
                        : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                    }`}>
                    {c.name}
                  </button>
                ))}
                {carrierList.length === 0 && (
                  <span className="col-span-2 text-xs text-slate-400 py-2">No carriers are enabled on your plan yet.</span>
                )}
              </div>
            </div>

            <div className="space-y-3.5">
              <div className="space-y-1.5">
                <label className={labelCls}>API key</label>
                <input className={`${inputCls} font-mono`} value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Secret key</label>
                <div className="relative">
                  <input className={`${inputCls} pr-10`} type={showSecret ? 'text' : 'password'}
                    value={secretKey} onChange={(e) => setSecretKey(e.target.value)} />
                  <button type="button" onClick={() => setShowSecret((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2.5 bg-slate-50 rounded-xl px-3.5 py-3">
              <Info size={15} className="text-slate-400 shrink-0 mt-0.5" />
              <span className="text-xs text-slate-500 leading-relaxed">
                Keys are checked against the carrier before they're saved, and stored encrypted.{' '}
                <span className="font-medium text-slate-700">Skip and you keep everything except live rates and labels.</span>
              </span>
            </div>

            <div className="h-px bg-slate-100" />
            <div className="flex items-center justify-between">
              <button className="flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-700"
                onClick={() => { setError(null); setStep(1); }}>
                <ArrowLeft size={14} /> Back
              </button>
              <div className="flex items-center gap-2">
                <button className={secondaryCls} onClick={() => { setError(null); setStep(3); }}>Skip for now</button>
                <button className={primaryCls} disabled={!carrierReady || saving} onClick={saveCarrier}>
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                  Verify and continue
                </button>
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="space-y-1.5">
              <h2 className="text-lg font-semibold text-slate-900">Connect your store</h2>
              <p className="text-[13px] text-slate-500 leading-relaxed">
                Orders and the product catalog are pulled from here. Nothing is written back to the store without your say-so.
              </p>
            </div>

            <div className="space-y-3.5">
              <div className="space-y-1.5">
                <label className={labelCls}>Platform</label>
                <div className="grid grid-cols-4 gap-2">
                  {PLATFORMS.map((p) => (
                    <button key={p} type="button" onClick={() => setPlatform(p)}
                      className={`rounded-xl border-[1.5px] px-2 py-2.5 text-xs transition-colors ${
                        platform === p
                          ? 'border-slate-900 bg-slate-50 text-slate-900 font-semibold'
                          : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                      }`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className={labelCls}>Store URL</label>
                <input className={inputCls} value={storeUrl} placeholder="https://www.example.com"
                  onChange={(e) => setStoreUrl(e.target.value)} />
                <span className="text-[11px] text-slate-400">The storefront root, not the admin URL.</span>
              </div>

              <div className="space-y-1.5">
                <label className={labelCls}>Access token</label>
                <div className="relative">
                  <input className={`${inputCls} pr-10`} type={showToken ? 'text' : 'password'}
                    value={token} onChange={(e) => setToken(e.target.value)} />
                  <button type="button" onClick={() => setShowToken((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2.5 bg-slate-50 rounded-xl px-3.5 py-3">
              <Info size={15} className="text-slate-400 shrink-0 mt-0.5" />
              <span className="text-xs text-slate-500 leading-relaxed">
                The first sync pulls your catalog and open orders — usually a few minutes.{' '}
                <span className="font-medium text-slate-700">Skip and you can still ship, you just enter orders by hand.</span>
              </span>
            </div>

            <div className="h-px bg-slate-100" />
            <div className="flex items-center justify-between">
              <button className="flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-700"
                onClick={() => { setError(null); setStep(2); }}>
                <ArrowLeft size={14} /> Back
              </button>
              <div className="flex items-center gap-2">
                <button className={secondaryCls} onClick={() => { setError(null); setStep(4); }}>Skip for now</button>
                <button className={primaryCls} disabled={!storeReady || saving} onClick={saveStore}>
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                  Verify and finish
                </button>
              </div>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div className="space-y-1.5">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mb-1">
                <Check size={20} className="text-emerald-600" strokeWidth={2.6} />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">You're ready to ship</h2>
              <p className="text-[13px] text-slate-500 leading-relaxed">
                Quoting is live against your address.
                {(!done.carrier || !done.store) && ' Some things are still outstanding.'}
              </p>
            </div>

            <div className="space-y-2">
              {[
                { title: 'Shipping address', detail: `${address.city || '—'} ${address.state} ${address.zip}`, ok: true },
                { title: 'Carrier account', detail: done.carrier ? 'Connected' : 'Skipped — no live rates or labels', ok: done.carrier },
                { title: 'Store connection', detail: done.store ? 'Connected — first sync will run shortly' : 'Skipped — orders are entered by hand', ok: done.store },
              ].map((r) => (
                <div key={r.title} className={`flex items-center gap-3 rounded-xl px-3.5 py-3 border ${r.ok ? 'border-slate-100 bg-white' : 'border-slate-200 bg-slate-50'}`}>
                  <div className={`w-[22px] h-[22px] rounded-full flex items-center justify-center shrink-0 ${r.ok ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                    {r.ok
                      ? <Check size={12} className="text-emerald-600" strokeWidth={3} />
                      : <span className="w-2.5 h-[3px] bg-slate-400 rounded-full" />}
                  </div>
                  <div className="flex-grow">
                    <div className="text-[13px] font-medium text-slate-900">{r.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{r.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="h-px bg-slate-100" />
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Settings → Integrations, any time</span>
              <button className={primaryCls} onClick={onClose}>Go to dashboard</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
